import React, { useState } from "react";
import { Link } from "react-router-dom";
import DesignPreviewShell from "@/components/DesignPreviewShell";
import { useDesignTheme } from "@/hooks/useDesignTheme";

/**
 * Field Radar × ContractorPlus — Work Order Detail Preview
 *
 * READ-ONLY VISUAL PROTOTYPE. Full-fat WO page:
 *  - Hero with WO#, address, status, assignee
 *  - Big photo gallery with before/after tabs
 *  - Description / write-up card
 *  - Checklist / issues
 *  - Notes thread
 *  - Sidebar: meta, cost, timing, source, tenant, actions
 *  - Timeline (activity log)
 *  - Receipts / materials
 *
 * Route: /admin/design-preview/wo
 */

const STATUS = { k: "in_progress", label: "In Progress", color: "#B12FBB", dot: "#B12FBB" };

const PHOTOS_BEFORE = [
  { u: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=600&q=70", t: "Faucet base — corrosion" },
  { u: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=600&q=70", t: "Under-sink · shutoff" },
  { u: "https://images.unsplash.com/photo-1584622781564-1d987f7333c1?w=600&q=70", t: "Drain assembly" },
  { u: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=70", t: "Wide shot" },
];
const PHOTOS_AFTER = [
  { u: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=600&q=70", t: "New faucet installed" },
  { u: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&q=70", t: "Clean install · tight seal" },
];

const CHECKLIST = [
  { done: true,  label: "Confirm shutoff isolates cold + hot",       by: "Marcus", at: "8:52 AM" },
  { done: true,  label: "Remove old faucet + supply lines",           by: "Marcus", at: "9:14 AM" },
  { done: true,  label: "Photo damaged bracket for landlord approval", by: "Marcus", at: "9:21 AM" },
  { done: false, label: "Install replacement faucet (Moen 87233)",    by: null,     at: null },
  { done: false, label: "Silicone bead around base + test",            by: null,     at: null },
  { done: false, label: "Final photos · both angles",                  by: null,     at: null },
];

const NOTES = [
  { who: "Marcus D.", role: "Field", when: "9:22 AM", avatar: "#3B82F6", text: "Bracket is stripped — supply line seized. Bought replacement from HD (receipt attached). Water back on 9:35, all clear.", pinned: true },
  { who: "Andrew R.", role: "Admin", when: "9:18 AM", avatar: "#FF6A2B", text: "Green-lit the Moen 87233. Tenant OK to be waterless until 11am." },
  { who: "Marcus D.", role: "Field", when: "8:44 AM", avatar: "#3B82F6", text: "On-site. Meeting tenant Test T. now. Photos coming." },
];

const TIMELINE = [
  { when: "9:35 AM", icon: "💧", color: "#3B82F6", text: "Water restored · leak-check clean" },
  { when: "9:22 AM", icon: "📝", color: "#8B5CF6", text: "Marcus added a note · pinned" },
  { when: "9:14 AM", icon: "📸", color: "#22C6A6", text: "6 before-photos uploaded" },
  { when: "9:03 AM", icon: "💰", color: "#F5B93F", text: "Receipt attached · Home Depot $87.33" },
  { when: "8:44 AM", icon: "⏱", color: "#00C4CC", text: "Marcus clocked in · GPS confirmed" },
  { when: "8:12 AM", icon: "👷", color: "#FF6A2B", text: "Assigned to Marcus by Andrew" },
  { when: "Sep 22 · 7:41 AM", icon: "🔗", color: "#B12FBB", text: "Pulled from ClickUp · #3711" },
  { when: "Sep 21 · 4:02 PM", icon: "📥", color: "#3B82F6", text: "Created from tenant SMS · via Twilio" },
];

const RECEIPTS = [
  { vendor: "Home Depot", desc: "Moen 87233 kitchen faucet + supply lines", amount: 87.33, when: "9:03 AM", category: "materials", ocr: "AUTO" },
  { vendor: "Home Depot", desc: "Plumber's putty + silicone", amount: 12.44, when: "9:04 AM", category: "materials", ocr: "AUTO" },
];

export default function WorkOrderDetailPreview() {
  const { theme } = useDesignTheme();
  const [photoTab, setPhotoTab] = useState("before");
  const [lightbox, setLightbox] = useState(null);
  const photos = photoTab === "before" ? PHOTOS_BEFORE : PHOTOS_AFTER;

  return (
    <DesignPreviewShell>
      <div className="font-sans">
        {/* ── HERO ─────────────────────────────────────────── */}
        <div className={`${theme.gradient} pt-6 pb-32 sm:pt-8 sm:pb-40 px-4 sm:px-6 relative`}>
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-2 text-white/70 text-[10px] font-black uppercase tracking-widest">
              <Link to="/admin/design-preview" className="hover:text-white">← Dashboard</Link>
              <span>·</span>
              <Link to="/admin/design-preview/jobs" className="hover:text-white">Work Orders</Link>
              <span>·</span>
              <span>#3711-1</span>
            </div>
            <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white/70 text-sm font-black">#3711-1</span>
                  <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full backdrop-blur bg-white/15 text-white text-[10px] font-black uppercase tracking-widest">
                    <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse"/>
                    {STATUS.label}
                  </span>
                  <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-white/10 backdrop-blur text-white text-[10px] font-black uppercase tracking-widest">
                    🔥 High priority
                  </span>
                  <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-white/10 backdrop-blur text-white/80 text-[10px] font-black uppercase tracking-widest">
                    via AppFolio
                  </span>
                </div>
                <h1 className="text-white font-black text-2xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-2">
                  Garbage disposal replacement
                </h1>
                <div className="text-white/80 text-sm font-medium mt-1">
                  📍 <Link to="/admin/design-preview/property" className="hover:underline">1230 Anyon St · Philadelphia, PA 19125</Link> · Test T.
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <button className="h-9 px-3 rounded-full bg-white/15 backdrop-blur text-white text-[10px] font-black uppercase tracking-widest hover:bg-white/25" data-testid="dp-wo-print">🖨 Print</button>
                <button className="h-9 px-3 rounded-full bg-white/15 backdrop-blur text-white text-[10px] font-black uppercase tracking-widest hover:bg-white/25" data-testid="dp-wo-share">🔗 Share</button>
                <button className="h-9 px-3 rounded-full bg-white/15 backdrop-blur text-white text-[10px] font-black uppercase tracking-widest hover:bg-white/25" data-testid="dp-wo-adjust">⏱ Adjust times</button>
                <button className="h-9 px-4 rounded-full bg-[#00C4CC] hover:bg-[#00a8af] text-[#0F1F3A] text-[10px] font-black uppercase tracking-widest" data-testid="dp-wo-close">
                  ✓ Close WO
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── BODY ────────────────────────────────────────── */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 -mt-24 pb-16 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* ═══ MAIN COLUMN ═══════════════════════════ */}
            <div className="lg:col-span-2 space-y-4">
              {/* Photos card */}
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] overflow-hidden" data-testid="dp-wo-photos">
                <div className="p-4 flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-black text-[#0F1F3A]">Photos</h3>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">
                    {PHOTOS_BEFORE.length + PHOTOS_AFTER.length} total
                  </span>
                  <div className="ml-auto inline-flex bg-slate-100 rounded-full p-1">
                    {[
                      { k: "before", label: `Before · ${PHOTOS_BEFORE.length}` },
                      { k: "after",  label: `After · ${PHOTOS_AFTER.length}` },
                      { k: "all",    label: "All" },
                    ].map(t => (
                      <button
                        key={t.k}
                        onClick={() => setPhotoTab(t.k)}
                        data-testid={`dp-wo-photo-tab-${t.k}`}
                        className={`h-7 px-3 rounded-full text-[10px] font-black uppercase tracking-widest transition-colors ${
                          photoTab === t.k ? "bg-white text-[#0F1F3A] shadow-sm" : "text-[#8B93A7] hover:text-[#0F1F3A]"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <button className="h-9 w-9 rounded-full bg-[#00C4CC] text-[#0F1F3A] font-black hover:bg-[#00a8af]" title="Upload photos">+</button>
                </div>
                <div className="px-4 pb-4 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                  {photos.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => setLightbox(p)}
                      data-testid={`dp-wo-photo-${i}`}
                      className="group relative aspect-square rounded-xl overflow-hidden bg-slate-100 hover:ring-2 hover:ring-[#00C4CC]"
                    >
                      <img src={p.u} alt={p.t} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"/>
                      <div className="absolute bottom-0 left-0 right-0 p-2 text-left">
                        <div className="text-[10px] font-black uppercase tracking-widest text-white truncate">{p.t}</div>
                      </div>
                      <span className="absolute top-1.5 right-1.5 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-white/90 text-[#0F1F3A]">
                        {photoTab === "after" ? "After" : "Before"}
                      </span>
                    </button>
                  ))}
                  {/* Add tile */}
                  <button className="aspect-square rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center gap-1 text-[#8B93A7] hover:border-[#00C4CC] hover:text-[#00C4CC] hover:bg-[#00C4CC]/5">
                    <span className="text-3xl">+</span>
                    <span className="text-[9px] font-black uppercase tracking-widest">Add photo</span>
                  </button>
                </div>
              </div>

              {/* Description / write-up */}
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-5" data-testid="dp-wo-description">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-black text-[#0F1F3A]">Description</h3>
                  <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#F5B93F]/20 text-[#8B6D1F]">AF sync</span>
                  <button className="ml-auto text-[10px] font-black uppercase tracking-widest text-[#00807a] hover:text-[#0F1F3A]">✎ Edit</button>
                </div>
                <p className="text-sm text-[#0F1F3A] leading-relaxed">
                  Tenant reports the kitchen sink faucet is dripping constantly and the handle is loose. Water is
                  pooling under the sink and the bracket appears corroded. Please replace the faucet unit (approved
                  vendor list) and check the supply lines while you're in there. Tenant available Mon-Fri 8am-6pm.
                </p>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3 flex-wrap text-[11px] font-medium text-[#8B93A7]">
                  <span>📝 Written by <b className="text-[#0F1F3A]">AppFolio Sync</b></span>
                  <span>•</span>
                  <span>Received Sep 21 · 4:02 PM</span>
                  <span>•</span>
                  <span>3 edits</span>
                </div>
              </div>

              {/* Checklist / issues */}
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-5" data-testid="dp-wo-checklist">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-lg font-black text-[#0F1F3A]">Checklist</h3>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">
                    3 of 6 · 50%
                  </span>
                  <div className="ml-auto w-32 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#22C6A6] to-[#00C4CC]" style={{ width: "50%" }}/>
                  </div>
                </div>
                <div className="space-y-1">
                  {CHECKLIST.map((c, i) => (
                    <div key={i} data-testid={`dp-wo-check-${i}`}
                      className={`flex items-start gap-2.5 p-2 rounded-lg hover:bg-slate-50 group`}>
                      <button className={`mt-0.5 h-5 w-5 rounded-md flex items-center justify-center flex-shrink-0 border-2 ${
                        c.done ? "bg-[#22C6A6] border-[#22C6A6] text-white" : "border-slate-300 bg-white"
                      }`}>
                        {c.done && <span className="text-[10px] font-black">✓</span>}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold ${c.done ? "text-[#8B93A7] line-through" : "text-[#0F1F3A]"}`}>
                          {c.label}
                        </div>
                        {c.done && (
                          <div className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7] mt-0.5">
                            {c.by} · {c.at}
                          </div>
                        )}
                      </div>
                      <button className="opacity-0 group-hover:opacity-100 text-[#8B93A7] hover:bg-slate-100 h-7 w-7 rounded-full text-sm">⋯</button>
                    </div>
                  ))}
                </div>
                <button className="mt-2 text-[10px] font-black uppercase tracking-widest text-[#00807a] hover:text-[#0F1F3A]">
                  + Add checklist item
                </button>
              </div>

              {/* Notes thread */}
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-5" data-testid="dp-wo-notes">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-lg font-black text-[#0F1F3A]">Notes & Updates</h3>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{NOTES.length}</span>
                </div>

                {/* Composer */}
                <div className="mb-4 bg-slate-50 rounded-xl p-3">
                  <textarea
                    placeholder="Add a note · @mention · ⌘Enter to post"
                    rows={2}
                    className="w-full bg-transparent text-sm text-[#0F1F3A] placeholder:text-[#8B93A7] focus:outline-none resize-none"
                    data-testid="dp-wo-note-input"
                  />
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    <button className="h-7 px-2 rounded-full hover:bg-white text-[#8B93A7] text-xs">📎</button>
                    <button className="h-7 px-2 rounded-full hover:bg-white text-[#8B93A7] text-xs">📸</button>
                    <button className="h-7 px-2 rounded-full hover:bg-white text-[#8B93A7] text-xs">📍</button>
                    <button className="h-7 px-2 rounded-full hover:bg-white text-[#8B93A7] text-xs">📌 Pin</button>
                    <label className="ml-auto text-[10px] font-medium text-[#8B93A7] flex items-center gap-1.5">
                      <input type="checkbox" className="rounded"/> Notify landlord
                    </label>
                    <button className="h-7 px-3 rounded-full bg-[#0F1F3A] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#1a2d4f]">
                      Post
                    </button>
                  </div>
                </div>

                {/* Notes list */}
                <div className="space-y-3">
                  {NOTES.map((n, i) => (
                    <div key={i} data-testid={`dp-wo-note-${i}`} className={`flex gap-3 p-3 rounded-xl ${n.pinned ? "bg-[#F5B93F]/8 border border-[#F5B93F]/30" : "hover:bg-slate-50"}`}>
                      <div className="h-9 w-9 rounded-full flex items-center justify-center text-white font-black text-xs flex-shrink-0" style={{ background: n.avatar }}>
                        {n.who.split(" ").map(w => w[0]).join("")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-black text-[#0F1F3A]">{n.who}</span>
                          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-slate-100 text-[#8B93A7]">{n.role}</span>
                          {n.pinned && <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#F5B93F]/25 text-[#8B6D1F]">📌 Pinned</span>}
                          <span className="text-[10px] font-medium text-[#8B93A7] ml-auto">{n.when}</span>
                        </div>
                        <div className="text-sm text-[#0F1F3A] mt-1 leading-relaxed">{n.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Receipts / materials */}
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-5" data-testid="dp-wo-receipts">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-lg font-black text-[#0F1F3A]">Receipts · Materials</h3>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{RECEIPTS.length}</span>
                  <span className="ml-auto text-sm font-black text-[#22C6A6]">
                    ${RECEIPTS.reduce((s, r) => s + r.amount, 0).toFixed(2)}
                  </span>
                </div>
                <div className="space-y-2">
                  {RECEIPTS.map((r, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100">
                      <div className="h-10 w-10 rounded-lg bg-[#F5B93F]/15 flex items-center justify-center text-lg flex-shrink-0">🧾</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-black text-[#0F1F3A]">{r.vendor}</span>
                          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#22C6A6]/15 text-[#0F7F6A]">
                            OCR {r.ocr}
                          </span>
                        </div>
                        <div className="text-xs text-[#8B93A7] truncate">{r.desc}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-black text-[#0F1F3A]">${r.amount.toFixed(2)}</div>
                        <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">{r.when}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="mt-3 text-[10px] font-black uppercase tracking-widest text-[#00807a] hover:text-[#0F1F3A]">
                  + Upload receipt · OCR runs automatically
                </button>
              </div>
            </div>

            {/* ═══ RIGHT COLUMN ═══════════════════════════ */}
            <div className="space-y-4">
              {/* Quick actions (moved to top) */}
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-3" data-testid="dp-wo-quick-actions">
                <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7] mb-2 px-1">Quick actions</div>
                <div className="grid grid-cols-3 gap-1">
                  {[
                    { i: "📞", l: "Call tenant" },
                    { i: "💬", l: "SMS tenant" },
                    { i: "🗺", l: "Directions" },
                    { i: "📋", l: "Duplicate" },
                    { i: "🔀", l: "Merge" },
                    { i: "🗑", l: "Cancel" },
                  ].map(a => (
                    <button key={a.l} className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-slate-50">
                      <span className="text-xl">{a.i}</span>
                      <span className="text-[9px] font-black uppercase tracking-widest text-[#0F1F3A]">{a.l}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Meta card */}
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-5" data-testid="dp-wo-meta">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-black text-[#0F1F3A]">Details</h3>
                  <button className="text-[10px] font-black uppercase tracking-widest text-[#00807a] hover:text-[#0F1F3A]">✎ Edit</button>
                </div>
                <div className="space-y-3">
                  <MetaRow label="Assigned to" val={<TechChip name="Marcus D." bg="#3B82F6" sub="Tech · clocked in"/>} />
                  <MetaRow label="Landlord"    val={<span className="text-sm font-black text-[#0F1F3A]">ProCall Rentals LLC</span>} />
                  <MetaRow label="Tenant"      val={
                    <div>
                      <div className="text-sm font-black text-[#0F1F3A]">Test T.</div>
                      <div className="text-[11px] text-[#8B93A7]">(215) 555-1234</div>
                    </div>
                  } />
                  <MetaRow label="Priority"    val={<span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-[#E64C5A]/15 text-[#B91C1C] text-[10px] font-black uppercase tracking-widest">🔥 High</span>} />
                  <MetaRow label="Source"      val={
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-slate-100 text-[#0F1F3A]">AppFolio</span>
                      <span className="text-[10px] text-[#8B93A7]">SR #40219</span>
                    </div>
                  } />
                  <MetaRow label="ClickUp"     val={<span className="text-xs font-bold text-[#00807a]">#3711 · synced</span>} />
                  <MetaRow label="Category"    val={<span className="text-sm font-bold text-[#0F1F3A]">Plumbing · Faucet</span>} />
                </div>
              </div>

              {/* Timing */}
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-5" data-testid="dp-wo-timing">
                <h3 className="text-base font-black text-[#0F1F3A] mb-3">Timing</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="h-2 w-2 rounded-full bg-[#00C4CC]"/>
                    <span className="text-[#8B93A7] font-medium">Received</span>
                    <span className="ml-auto font-black text-[#0F1F3A]">Sep 21 · 4:02 PM</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="h-2 w-2 rounded-full bg-[#FF6A2B]"/>
                    <span className="text-[#8B93A7] font-medium">Assigned</span>
                    <span className="ml-auto font-black text-[#0F1F3A]">Sep 22 · 8:12 AM</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="h-2 w-2 rounded-full bg-[#B12FBB] animate-pulse"/>
                    <span className="text-[#8B93A7] font-medium">Started</span>
                    <span className="ml-auto font-black text-[#0F1F3A]">Sep 22 · 8:44 AM</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs opacity-40">
                    <span className="h-2 w-2 rounded-full bg-[#22C6A6]"/>
                    <span className="text-[#8B93A7] font-medium">Completed</span>
                    <span className="ml-auto font-black text-[#0F1F3A]">—</span>
                  </div>
                  <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-2 text-xs">
                    <span className="text-[#8B93A7] font-black uppercase tracking-widest text-[9px]">Elapsed</span>
                    <span className="ml-auto font-black text-[#0F1F3A]">2h 18m · live</span>
                  </div>
                </div>
              </div>

              {/* Cost / Rebill */}
              <div className="bg-gradient-to-br from-[#22C6A6] to-[#0F7F6A] rounded-2xl p-5 text-white shadow-[0_8px_30px_rgb(0,0,0,0.15)]" data-testid="dp-wo-cost">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-black">Rebill Preview</h3>
                  <span className="text-[9px] font-black uppercase tracking-widest bg-white/15 px-2 py-0.5 rounded-full">DRAFT</span>
                </div>
                <div className="space-y-1.5 text-sm">
                  <Row2 l="Labor · 2h 18m @ $85" r="$195.50"/>
                  <Row2 l="Materials · 2 receipts" r="$99.77"/>
                  <Row2 l="Markup · 15%" r="$44.29"/>
                  <div className="my-2 h-px bg-white/20"/>
                  <Row2 l={<b>Total to landlord</b>} r={<b className="text-lg">$339.56</b>}/>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button className="h-9 rounded-full bg-white text-[#0F7F6A] text-[10px] font-black uppercase tracking-widest hover:bg-slate-100" data-testid="dp-wo-preview-pdf">📄 Preview PDF</button>
                  <button className="h-9 rounded-full bg-white/15 backdrop-blur text-white text-[10px] font-black uppercase tracking-widest hover:bg-white/25 border border-white/30" data-testid="dp-wo-send-rebill">Send rebill →</button>
                </div>
              </div>

              {/* Timeline / activity */}
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-5" data-testid="dp-wo-timeline">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-base font-black text-[#0F1F3A]">Activity</h3>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{TIMELINE.length} events</span>
                </div>
                <div className="relative space-y-3">
                  <div className="absolute left-[15px] top-2 bottom-2 w-px bg-slate-200"/>
                  {TIMELINE.map((t, i) => (
                    <div key={i} className="flex gap-3 relative" data-testid={`dp-wo-timeline-${i}`}>
                      <div className="h-8 w-8 rounded-full flex items-center justify-center text-sm flex-shrink-0 relative z-10 ring-4 ring-white"
                        style={{ background: t.color + "22", color: t.color }}>
                        <span>{t.icon}</span>
                      </div>
                      <div className="flex-1 min-w-0 pt-1">
                        <div className="text-xs text-[#0F1F3A] font-semibold leading-snug">{t.text}</div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7] mt-0.5">{t.when}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="mt-3 w-full h-8 rounded-full bg-slate-50 hover:bg-slate-100 text-[10px] font-black uppercase tracking-widest text-[#0F1F3A]">
                  Load older events
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-white/40 text-xs font-medium">
              Preview only · <Link to="/admin/design-preview/jobs" className="text-[#00C4CC]">back to Work Orders board</Link>
            </p>
          </div>
        </div>
      </div>

      {/* ── LIGHTBOX ─────────────────────────────────── */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="dp-wo-lightbox">
          <button className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 backdrop-blur text-white text-2xl hover:bg-white/25">×</button>
          <div className="max-w-4xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <img src={lightbox.u} alt={lightbox.t} className="rounded-2xl max-h-[80vh] mx-auto"/>
            <div className="text-center text-white/80 mt-3 text-sm font-medium">{lightbox.t}</div>
          </div>
        </div>
      )}
    </DesignPreviewShell>
  );
}

/* ── Sub components ────────────────────────────────── */
function MetaRow({ label, val }) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7] w-20 flex-shrink-0 pt-1">{label}</div>
      <div className="flex-1 min-w-0">{val}</div>
    </div>
  );
}

function TechChip({ name, bg, sub }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-7 w-7 rounded-full flex items-center justify-center text-white font-black text-xs" style={{ background: bg }}>
        {name.split(" ").map(w => w[0]).join("")}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-black text-[#0F1F3A] leading-tight truncate">{name}</div>
        <div className="text-[10px] font-medium text-[#8B93A7] leading-tight flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-[#22C6A6] animate-pulse"/>
          {sub}
        </div>
      </div>
    </div>
  );
}

function Row2({ l, r }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/90">{l}</span>
      <span className="text-white font-semibold">{r}</span>
    </div>
  );
}
